import { css, keyframes } from 'styled-components';

import { owsVar } from './themeRegistry';

import {
  COLORS,
  PULSE,
  ANIM,
  SCROLLBAR,
  HOVER_GLOW_RADIUS_PX,
  HOVER_BORDER_PX,
  HOVER_BORDER_GLOW_RADIUS_PX,
  REDUCED_MOTION,
  SPACE,
} from './tokens';

/**
 * Reusable styled-components fragments for patterns that recur across the area.
 * Keeps the visual primitives in one place (see docs/components.md) so a thumb
 * fallback or card surface changes everywhere from a single edit.
 */

/** Cover-fit background artwork with the area's loading fallback colour. */
export const coverImage = (src: string) => css`
  background-image: url(${src});
  background-size: cover;
  background-position: center;
  background-color: ${COLORS.surfaceFainter};
`;

/**
 * The "soft card" surface — rounded, borderless, faint fill. Used by the
 * KPI tiles, budget tiles, campaign/asset list cards and self-managed channels.
 * Padding and layout are set by the consumer.
 */
export const softCard = css`
  border-radius: 14px;
  background: ${COLORS.surfaceSoft};
`;

/**
 * Pulse outer card — the zinc card with a 32% black wash, borderless
 * (source `.card`). Every Pulse section sits on this surface; padding is set by
 * the consumer.
 */
export const pulseCard = css`
  background: linear-gradient(${PULSE.cardOverlay}, ${PULSE.cardOverlay}), ${PULSE.cardBase};
  border-radius: 16px;
`;

/**
 * The two border-sweep rotations (see hoverWash). They animate the registered
 * `<angle>` props `--ows-sweep-a/-b` (typed in hooks/usePointerGlow so they
 * interpolate), which orient the `conic-gradient`s in the border ring. The angle
 * stops are deliberately UNEVEN — equal time, unequal travel — so each sweep's
 * angular speed rises and falls instead of marching at a constant rate; `ease-in-out`
 * smooths the accel/decel. `b` runs 360 → 0 (counter-clockwise) and on a different
 * period, so the two arcs cross at a slowly-shifting beat rather than locking up.
 */
const borderSweepA = keyframes`
  0%   { --ows-sweep-a: 0deg; }
  28%  { --ows-sweep-a: 138deg; }
  53%  { --ows-sweep-a: 196deg; }
  78%  { --ows-sweep-a: 324deg; }
  100% { --ows-sweep-a: 360deg; }
`;

const borderSweepB = keyframes`
  0%   { --ows-sweep-b: 360deg; }
  31%  { --ows-sweep-b: 246deg; }
  59%  { --ows-sweep-b: 154deg; }
  83%  { --ows-sweep-b: 48deg; }
  100% { --ows-sweep-b: 0deg; }
`;

/* hoverWash's colours on light (see its light rules), set on the wash's own layers so nothing the card holds reads
   them. */
const shineVar = (key: string, value: string): string => `${owsVar('COLORS', key)}: ${value};`;
const SHINE_RING = [
  shineVar('hoverBorder', `color-mix(in srgb, ${COLORS.text} 16%, transparent)`),
  shineVar('hoverBorderLit', 'white'),
  shineVar('hoverBorderSweep', 'color-mix(in srgb, white 92%, transparent)'),
  shineVar('hoverBorderSweepSoft', 'color-mix(in srgb, white 60%, transparent)'),
].join('\n');
const SHINE_POOL = [
  shineVar('hoverGlow', 'color-mix(in srgb, white 38%, transparent)'),
  // The card a touch darker while hovered, so the white spotlight shows over it (the user, 2026-10-21).
  shineVar('hoverWash', `color-mix(in srgb, ${COLORS.text} 3%, transparent)`),
].join('\n');

/**
 * Hover affordance for a clickable card: a big, soft radial glow that follows the
 * cursor and lights up the surface as the pointer moves across it, over a faint
 * uniform wash. Fades in on hover (and keyboard focus). Implemented as a pointer-
 * events-none `::after` layer at `z-index: -1`: above the card's own background but
 * UNDER everything the card holds, so it never washes its content (a card whose art
 * covers it opaquely shows only the border ring). It picks up the `border-radius` via
 * `inherit`. The consumer just needs to apply this to a clickable card root.
 *
 * The glow position comes from the `--ows-glow-x/-y` custom properties, written
 * by a single global controller (`hooks/usePointerGlow`, mounted once in the
 * layout) onto whichever glow card the cursor is over. The same controller writes
 * `--ows-glow-scale` (1 at the card's centre → smaller toward its edges) so the
 * pool tucks in as it nears a border. `--ows-glow-card: 1` is the marker that
 * controller looks for; it's a non-inheriting registered property, so only the
 * card root reports it (descendants don't), letting the controller resolve the
 * exact card from the event target. With no controller / on an unsupporting
 * browser the properties keep their defaults, degrading to a soft centred glow.
 *
 * The glow is white in both themes. On dark the `::after` screens a faint wash and a
 * white spotlight over the card; on light (white cannot brighten white) the card
 * darkens a touch under a white spotlight, and the `::before` ring turns a soft grey
 * with the white shine running over it (SHINE_RING / SHINE_POOL). `isolation: isolate`
 * makes the card the stacking context the `z-index: -1` layer needs (and tames
 * Safari's blend flicker).
 *
 * A second `::before` layer draws a 1px hairline border, masked to just the ring (the
 * padding/border-box `mask-composite` trick) and composed of four stacked background
 * layers (top → bottom): (1) a radial *proximity* highlight centred on the same
 * `--ows-glow-x/-y` cursor position, so the edge nearest the pointer brightens to
 * `hoverBorderLit`; (2,3) two counter-rotating `conic-gradient` *sweeps* that drift a
 * soft `hoverBorderSweep` arc around the perimeter while hovered (see `borderSweepA/B`)
 * — incommensurate periods + opposite spin make the shimmer organic and non-repeating;
 * (4) the faint `hoverBorder` baseline the rest of the ring rests at. Each upper layer
 * is transparent except where it contributes, so they compose additively. It tracks
 * the cursor for free off the controller vars (no per-card JS) and fades in on hover /
 * keyboard focus. No `mix-blend-mode` here — a crisp edge, not a wash — which also
 * keeps it clear of the Safari blend-layer issues the `::after` guards against.
 */
export const hoverWash = css`
  position: relative;
  isolation: isolate;
  --ows-glow-card: 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    /* Above the wash and any positioned content so the hairline stays crisp; it's a
       1px edge ring with pointer-events: none, so it never occludes the content. */
    z-index: 2;
    border-radius: inherit;
    /* The ring thickness: the gradients fill the whole box, the mask keeps only this
       outer padding band (border-box minus content-box). */
    padding: ${HOVER_BORDER_PX}px;
    background:
      /* (1) cursor-following proximity highlight — fades to transparent so the layers
         below show through where the pointer isn't. */
      radial-gradient(
        circle ${HOVER_BORDER_GLOW_RADIUS_PX}px at var(--ows-glow-x, 50%) var(--ows-glow-y, 50%),
        ${COLORS.hoverBorderLit} 0%,
        transparent 100%
      ),
      /* (2,3) the two drifting sweeps — a soft arc each, rotated by --ows-sweep-a/-b. */
      conic-gradient(
        from var(--ows-sweep-a, 0deg),
        transparent 0deg,
        ${COLORS.hoverBorderSweep} 22deg,
        transparent 50deg,
        transparent 360deg
      ),
      conic-gradient(
        from var(--ows-sweep-b, 0deg),
        transparent 0deg,
        ${COLORS.hoverBorderSweepSoft} 34deg,
        transparent 78deg,
        transparent 360deg
      ),
      /* (4) the faint always-on baseline the rest of the ring rests at. */
      ${COLORS.hoverBorder};
    /* Opaque white mask (works in both luminance and alpha mask modes); composite
       border-box XOR content-box → only the 1px ring paints. */
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0;
    pointer-events: none;
    /* Fade the ring in on hover; ease the lit point toward the cursor (same low-pass
       as the wash) so the illuminated segment glides along the edge. */
    transition:
      opacity ${ANIM.GLOW_FOLLOW_MS}ms ease,
      --ows-glow-x ${ANIM.GLOW_FOLLOW_MS}ms ease-out,
      --ows-glow-y ${ANIM.GLOW_FOLLOW_MS}ms ease-out;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    /* Under what the card holds (above its own background), so the wash and the spotlight never wash its content; the
       spotlight a white pool that shows on dark too (the user, 2026-10-21; light sets its own, see SHINE_POOL). */
    z-index: -1;
    ${owsVar('COLORS', 'hoverGlow')}: color-mix(in srgb, white 6%, transparent);
    border-radius: inherit;
    background:
      radial-gradient(
        circle calc(${HOVER_GLOW_RADIUS_PX}px * var(--ows-glow-scale, 1)) at
          var(--ows-glow-x, 50%) var(--ows-glow-y, 50%),
        ${COLORS.hoverGlow} 0%,
        transparent 100%
      ),
      ${COLORS.hoverWash};
    mix-blend-mode: screen;
    opacity: 0;
    pointer-events: none;
    /* Fade the whole layer on hover; ease the highlight toward the cursor (and its
       edge-shrink) so it glides rather than snaps (a soft low-pass on the pointer). */
    transition:
      opacity ${ANIM.GLOW_FOLLOW_MS}ms ease,
      --ows-glow-x ${ANIM.GLOW_FOLLOW_MS}ms ease-out,
      --ows-glow-y ${ANIM.GLOW_FOLLOW_MS}ms ease-out,
      --ows-glow-scale ${ANIM.GLOW_FOLLOW_MS}ms ease-out;
  }

  /* Light theme: white light cannot brighten a white card, so while hovered the border turns a soft grey and the
     shine (the lit point by the cursor, the drifting arcs) runs over that rim near full white; the card darkens a touch
     and the cursor's spotlight is a white pool over it, both under what the card holds (above its own background), so
     they never wash its content (the user, 2026-10-21, for every hover in the app). */
  :root[data-ows-theme='light'] &::before {
    ${SHINE_RING}
  }

  :root[data-ows-theme='light'] &::after {
    ${SHINE_POOL}
    mix-blend-mode: normal;
  }

  /* Respect reduced-motion: keep the glow + border, drop the pointer easing and the
     drifting sweep so the hairline is static. */
  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      transition: opacity ${ANIM.INTERACT_MS}ms ease;
    }
    &:hover::before,
    &:focus-visible::before {
      animation: none;
    }
  }

  &:hover::before,
  &:hover::after,
  &:focus-visible::before,
  &:focus-visible::after {
    opacity: 1;
  }

  /* Start the drifting sweeps only while hovered / focused — they're invisible at
     rest (opacity 0), so there's nothing to animate until the ring fades in. */
  &:hover::before,
  &:focus-visible::before {
    animation:
      ${borderSweepA} ${ANIM.BORDER_SWEEP_A_MS}ms ease-in-out infinite,
      ${borderSweepB} ${ANIM.BORDER_SWEEP_B_MS}ms ease-in-out infinite;
  }
`;

/**
 * Thin custom scrollbar — a 6px track with a rounded neutral thumb over a
 * transparent track. Shared by the Lumio message thread and the main content-area
 * page scroller so they stay identical. Apply to the element that OWNS the scroll
 * (the one with `overflow-y: auto`), not a clipping parent.
 *
 * (The configurator wizard has its own near-identical `thinScrollbar` in
 * `theme/configurator-keyframes.ts`, scoped to its separate token universe —
 * import THIS one from `theme/mixins` for the hand-written area.)
 */
export const thinScrollbar = css`
  scrollbar-width: thin;
  scrollbar-color: ${SCROLLBAR.thumb} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${SCROLLBAR.thumb};
    border-radius: 3px;
  }
`;

/**
 * Vertical fade mask — a real CSS `mask-image` (not an overlay element) that lets
 * a scroll container's content slide *under* a header/footer and dissolve into
 * them. Travels with whatever element it's applied to, so a pane using it stays
 * self-contained when reused elsewhere.
 *
 * `topPx`/`bottomPx` are the heights of the chrome the content hides behind: the
 * content is FULLY transparent across those whole bands (so nothing ever overlaps
 * the header/footer), then ramps to opaque over `fadePx` just inside them. Pair
 * it with `padding-top: topPx + fadePx` so the first row rests fully opaque.
 *
 * The gradient stops are mask ALPHA, not design colours (black = keep,
 * transparent = hide), so they aren't tokenised.
 */
export const fadeMaskY = (topPx: number, bottomPx: number, fadePx = 24) => {
  const gradient = `linear-gradient(
    to bottom,
    transparent 0,
    transparent ${topPx}px,
    #000 ${topPx + fadePx}px,
    #000 calc(100% - ${bottomPx + fadePx}px),
    transparent calc(100% - ${bottomPx}px),
    transparent 100%
  )`;
  return css`
    -webkit-mask-image: ${gradient};
    mask-image: ${gradient};
  `;
};

/**
 * The CONDITIONAL variant of {@link fadeMaskY}: each edge only ramps when there
 * is actually hidden content past it, so a list that fits (or one scrolled hard
 * to an end) keeps its first/last row crisp instead of permanently dimmed. The
 * caller owns the two booleans, recomputed from `scrollTop`/`scrollHeight` on
 * scroll.
 *
 * Use this for a self-contained scroll region whose content simply runs out;
 * use `fadeMaskY` when the content slides under fixed chrome of a known height.
 * Same alpha-not-colour rationale for the untokenised gradient stops.
 */
export const scrollFadeMaskY = (fadeTop: boolean, fadeBottom: boolean, fadePx = 24) => {
  const start = fadeTop ? `transparent 0, #000 ${fadePx}px` : '#000 0'; // design-allow: mask alpha
  const end = fadeBottom ? `#000 calc(100% - ${fadePx}px), transparent 100%` : '#000 100%'; // design-allow: mask alpha
  const gradient = fadeTop || fadeBottom ? `linear-gradient(to bottom, ${start}, ${end})` : 'none'; // design-allow: mask alpha
  return css`
    -webkit-mask-image: ${gradient};
    mask-image: ${gradient};
  `;
};

/**
 * {@link scrollFadeMaskY} across: a row that scrolls sideways fades out at the start and the end only while
 * there is more of it past that edge. The caller owns the two booleans (`useScrollEdges` computes them).
 */
export const scrollFadeMaskX = (fadeStart: boolean, fadeEnd: boolean, fadePx = 24) => {
  const start = fadeStart ? `transparent 0, #000 ${fadePx}px` : '#000 0'; // design-allow: mask alpha
  const end = fadeEnd ? `#000 calc(100% - ${fadePx}px), transparent 100%` : '#000 100%'; // design-allow: mask alpha
  const gradient = fadeStart || fadeEnd ? `linear-gradient(to right, ${start}, ${end})` : 'none'; // design-allow: mask alpha
  return css`
    -webkit-mask-image: ${gradient};
    mask-image: ${gradient};
  `;
};

/** A card's entrance: a fade with a short rise and a breath of scale, settling to no transform at all. */
const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(${SPACE[8]}) scale(0.99);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

/**
 * A card's picture that fades in once its pixels are ready instead of popping in. Opt-in and fail-open: only an
 * element marked `data-reveal` (the props of `revealOnLoad` / `revealOnFrame`, pages/creatives/reveal-media) is held
 * hidden, and only until it is marked `data-ready`. Reduced motion: shown without the fade.
 */
export const fadeInWhenReady = css`
  transition: opacity ${ANIM.CARD_MEDIA_FADE_MS}ms ${ANIM.EASE};

  &[data-reveal]:not([data-ready]) {
    opacity: 0;
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/**
 * The staggered entrance of a grid's n-th card (the Website and Marketing hubs, the asset libraries, the creatives
 * grids): it starts n stagger steps after the first, held hidden until its turn. `backwards` releases the card to its
 * own styles once it has entered, for a card that restyles its opacity or transform (an archived card is dimmed);
 * `both` keeps the last frame.
 */
export const cardEnterAt = (index: number, fill: 'both' | 'backwards' = 'both') => css`
  animation: ${cardEnter} ${ANIM.HUB_CARD_ENTER_MS}ms ${ANIM.EASE} ${fill};
  animation-delay: ${Math.min(index, ANIM.CARD_ENTER_STAGGER_MAX) * ANIM.HUB_CARD_STAGGER_MS}ms;

  ${REDUCED_MOTION} {
    animation: none;
  }
`;

/** On light, a subtle shadow under a card: a fine contact edge and a soft fall (the user, 2026-10-21). Home's score
 *  cards. */
export const lightCardShadow = css`
  :root[data-ows-theme='light'] & {
    box-shadow:
      0 1px 2px color-mix(in srgb, ${COLORS.text} 6%, transparent),
      0 12px 28px -12px color-mix(in srgb, ${COLORS.text} 16%, transparent);
  }
`;

/** lightCardShadow at a third of its opacity, for the wide score headers of the pages Home's cards open (the user,
 *  2026-10-22). */
export const lightPanelShadow = css`
  :root[data-ows-theme='light'] & {
    box-shadow:
      0 1px 2px color-mix(in srgb, ${COLORS.text} 2%, transparent),
      0 12px 28px -12px color-mix(in srgb, ${COLORS.text} 5.3%, transparent);
  }
`;
